import { useEffect, useRef } from 'react';

import {
  subscribeToSkillExecutionRealtime,
  subscribeToTenantSkillExecutionsRealtime,
} from './chat-realtime';

type SkillExecutionTarget = { id: string; scope: 'execution' } | { id: string; scope: 'tenant' };

interface SkillExecutionUpdatesOptions {
  accessToken: string | null | undefined;
  fallbackIntervalMs?: number | undefined;
  onError: (cause: unknown) => void;
  onReconcile: (signal: AbortSignal) => Promise<void>;
  reconcileImmediately?: boolean;
  target: SkillExecutionTarget | null;
}

/**
 * Treats Realtime as an invalidation signal and coalesces canonical REST rereads.
 * The optional slow interval only protects active runs from a disconnected socket.
 */
export function useSkillExecutionUpdates({
  accessToken,
  fallbackIntervalMs,
  onError,
  onReconcile,
  reconcileImmediately = false,
  target,
}: SkillExecutionUpdatesOptions): void {
  const reconcileRef = useRef(onReconcile);
  const errorRef = useRef(onError);
  reconcileRef.current = onReconcile;
  errorRef.current = onError;
  const targetId = target?.id;
  const targetScope = target?.scope;

  useEffect(() => {
    if (!accessToken || !targetId || !targetScope) return;
    const controller = new AbortController();
    let inFlight = false;
    let replayRequested = false;
    const reconcile = () => {
      if (inFlight) {
        replayRequested = true;
        return;
      }
      inFlight = true;
      void reconcileRef
        .current(controller.signal)
        .catch((cause: unknown) => {
          if (!controller.signal.aborted) errorRef.current(cause);
        })
        .finally(() => {
          inFlight = false;
          if (replayRequested && !controller.signal.aborted) {
            replayRequested = false;
            reconcile();
          }
        });
    };
    const unsubscribe =
      targetScope === 'execution'
        ? subscribeToSkillExecutionRealtime({
            accessToken,
            executionId: targetId,
            onChange: reconcile,
          })
        : subscribeToTenantSkillExecutionsRealtime({
            accessToken,
            onChange: reconcile,
            tenantId: targetId,
          });
    if (reconcileImmediately) reconcile();
    const timer = fallbackIntervalMs
      ? window.setInterval(reconcile, fallbackIntervalMs)
      : undefined;
    return () => {
      controller.abort();
      unsubscribe();
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [accessToken, fallbackIntervalMs, reconcileImmediately, targetId, targetScope]);
}
